import { useCallback, useMemo } from 'react'
import {
    ScoopTree,
    ScoopText,
    ScoopChip,
    ScoopSkeleton,
    ScoopButton,
} from '../../../../ui'
import { ReportState } from '../ReportState/ReportState'
import { useRunTree } from './useRunTree'
import { normalizeSeverity, severityLabel } from '../../utils/severity'
import styles from './RunBrowserTree.module.scss'

// Keyed by canonical severity (utils/severity.js); aliases like "Watch" normalize first.
const SEVERITY_CLASSES = {
    critical: 'severityDotCritical',
    severe: 'severityDotSevere',
    moderate: 'severityDotModerate',
    watch: 'severityDotWatch',
    mild: 'severityDotMild',
    healthy: 'severityDotHealthy',
}

export const RunBrowserTree = ({
    runs = [],
    groups = [],
    loadingRuns,
    onNodeSelect,
    selectedTreeNodeId = null,
    selectedEntityId = null,
    selectedRunPath = null,
    onNewRun,
}) => {
    const { treeData, getNodeById } = useRunTree(runs, groups)

    // Navigation leaves tracks (design principle 5): resolve the selected tree
    // node from whatever the main pane is showing. Tree-origin selections carry
    // the node id directly; scorecard/rollup-origin entity selections are
    // located by entityId + runPath (the tree only lists flagged entities, so
    // an unflagged entity simply yields no highlight).
    const selectedNodes = useMemo(() => {
        if (selectedTreeNodeId) {
            const node = getNodeById(selectedTreeNodeId)
            return node ? [node] : []
        }
        if (selectedEntityId) {
            const stack = [...treeData]
            let fallback = null
            while (stack.length > 0) {
                const node = stack.pop()
                if (node.nodeType === 'entity' && node.entityId === selectedEntityId) {
                    if (selectedRunPath && node.runPath === selectedRunPath) {
                        return [node]
                    }
                    if (!fallback) fallback = node
                }
                stack.push(...(node.children || []))
            }
            return fallback ? [fallback] : []
        }
        return []
    }, [selectedTreeNodeId, selectedEntityId, selectedRunPath, treeData, getNodeById])

    const handleNodeClick = useCallback(
        (node) => {
            // Group headers organize; dates/hierarchy/entities act.
            if (node.nodeType === 'run_group') return
            onNodeSelect?.(node)
        },
        [onNodeSelect]
    )

    const renderNode = useCallback((node) => {
        switch (node.nodeType) {
            case 'run_group':
                return <ScoopText className={styles.groupLabel}>{node.label}</ScoopText>

            case 'date':
                return (
                    <div className={styles.dateNode}>
                        <ScoopText className={styles.dateLabel}>{node.dateLabel}</ScoopText>
                        {node.flaggedCount > 0 && (
                            <span className={styles.flaggedBadge}>{node.flaggedCount} flagged</span>
                        )}
                    </div>
                )

            case 'hierarchy_node':
                return (
                    <div className={styles.hierarchyNode}>
                        <ScoopText className={styles.hierarchyLabel}>{node.label}</ScoopText>
                        <ScoopText className={styles.hierarchyMeta}>
                            {node.flaggedCount || 0}/{node.entityCount || 0} flagged
                        </ScoopText>
                    </div>
                )

            case 'entity':
                return (
                    <div className={styles.entityNode}>
                        <span
                            className={`${styles.severityDot} ${styles[SEVERITY_CLASSES[normalizeSeverity(node.severity)]] || ''}`}
                        />
                        <ScoopText className={styles.entityLabel}>{node.label}</ScoopText>
                        {/* Color never rides alone (design principle 4): the dot
                            always comes with the severity WORD. */}
                        {normalizeSeverity(node.severity) !== 'unknown' && (
                            <ScoopText className={styles.keyFinding}>
                                {node.keyFinding
                                    ? `${severityLabel(node.severity)}: ${node.keyFinding}`
                                    : severityLabel(node.severity)}
                            </ScoopText>
                        )}
                    </div>
                )

            default:
                return <ScoopText>{node.label}</ScoopText>
        }
    }, [])

    // Skeleton rows shaped like the tree (design §4), not a spinner.
    if (loadingRuns) {
        return (
            <div className={styles.skeletonList}>
                {['55%', '80%', '70%', '85%', '65%', '75%'].map((width, index) => (
                    <ScoopSkeleton
                        key={index}
                        variant="text"
                        width={width}
                        height={16}
                        className={index % 2 === 1 ? styles.skeletonIndent : ''}
                    />
                ))}
            </div>
        )
    }

    // Empty states resolve to an action (design §4) — never a dead end.
    if (treeData.length === 0) {
        return (
            <ReportState
                className={styles.treeState}
                title="No runs yet"
                message="Completed investigation runs appear here."
                action={
                    onNewRun ? (
                        <ScoopButton variant="default" size="sm" onClick={() => onNewRun('')}>
                            New run
                        </ScoopButton>
                    ) : undefined
                }
            />
        )
    }

    return (
        <div className={styles.container}>
            <ScoopTree
                data={treeData}
                searchable
                searchPlaceholder="Search runs..."
                searchSize="sm"
                searchPadding="8px 12px"
                expandedByDefault
                renderNode={renderNode}
                onNodeClick={handleNodeClick}
                selectedNodes={selectedNodes}
                className={styles.tree}
            />
        </div>
    )
}
